import {
    AccountInfo,
    ComputeBudgetProgram,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useEffect, useState } from 'react';
import { useUrlParams } from '../../hooks/useUrlParams';
import {
    createTokenPriorityFee,
    deployPoolJitoTipDefault,
    removeLiquidityPumpswapJitoTip,
    removeLiquidityPumpswapRemovePriorityGas,
    removeLiquidityPumpswapRemoveUnits,
    solManagerJitoTip,
    tokenManagerJitoTip,
} from '../../../ConfigFees';
import { usePumpswap } from '../../hooks/usePumpswap';
import { Currency, CurrencyAmount, SPL_ACCOUNT_LAYOUT, WSOL } from '@raydium-io/raydium-sdk';
import {
    AuthorityType,
    createAssociatedTokenAccountIdempotentInstruction,
    createAssociatedTokenAccountInstruction,
    createCloseAccountInstruction,
    createSetAuthorityInstruction,
    createSyncNativeInstruction,
    createTransferInstruction,
    getAssociatedTokenAddress,
    getAssociatedTokenAddressSync,
    TOKEN_2022_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
    unpackAccount,
    unpackMint,
} from '@solana/spl-token';
import {
    CreateTokenTransferToHolderDefaultPercentage,
    rapidPumpfunTimeoutRemoveSeconds,
    removeLiquidityPumspwapGoalReachedTicksToRemove,
    solManagerDisperseForBundleBuyFeesExtra,
    solManagerDisperseForMixWalletFees,
    solManagerVolumeBuyExtra,
} from '../../../ConfigConstants';
import { useSplToken } from '../../hooks/useSplToken';
import { useSolana } from '../../hooks/useSolana';
import { removeKeypairDuplicates } from '../../utils/remove-keypair-duplicates';
import { useBackend } from '../../hooks/useBackend';
import axios from 'axios';
import pvmb from '../../utils/pvmb';
import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes';
import getRandomizedArray from '../../utils/get-randomized-array';
import { useJito } from '../../hooks/useJito';
import { use0Slot } from '../../hooks/use0Slot';
import { pumpAmmProtocolFees } from '../../utils/constants';
import { bigIntToStringWithDecimals } from '../../utils/bigInt-to-string-with-decimals';
import CountdownTimer from '../../utils/timer';

const SCANNER_CHUNK_SIZE = 100;

export function RapidLaunchPumpswap({
    mainConnection,
    connections,
    totalWalletsBalance,
    walletLoaded,
    logMessage,
    deployedToken,
    wallet,
    bundleBuyWallets,
    funder,
    holder,
    freezer,
    volFunder,
    volumeBuyWallets,
    mixWallets,
    bundleBuyValues,
    volumeBuyAmounts,
    allWallets,
    setDeployedToken,
    setDeployedTokenName,
    setDeployedTokenSymbol,
    currentSlot,
}: {
    mainConnection: Connection;
    connections: Connection[];
    totalWalletsBalance: number;
    walletLoaded: boolean;
    logMessage: (message: string, type?: 'success' | 'error' | 'warn') => void;
    deployedToken: string;
    wallet: Keypair;
    bundleBuyWallets: Keypair[];
    funder: Keypair;
    holder: Keypair;
    freezer: Keypair;
    volFunder: Keypair | undefined;
    volumeBuyWallets: Keypair[];
    mixWallets: Keypair[];
    bundleBuyValues: number[];
    volumeBuyAmounts: number[];
    allWallets: Keypair[];
    setDeployedToken: React.Dispatch<React.SetStateAction<string>>;
    setDeployedTokenName: React.Dispatch<React.SetStateAction<string>>;
    setDeployedTokenSymbol: React.Dispatch<React.SetStateAction<string>>;
    currentSlot: number;
}) {
    const { getPoolId, getLpMint, buildRemoveLiquidityTx, buildCreatePoolTx, getSellIx } = usePumpswap();
    const { multipleSolanaTrasfersWithJito, getMultipleSolBalances, executeInstructionsRpc } = useSolana();
    const { parseToken } = useBackend();
    const { getTipInstructions, spamAndConfirmBundle } = useJito();
    const { send0SlotTransaction } = use0Slot();
    const { autoAction } = useUrlParams();

    const {
        getSigners,
        getUpdateMetadataInstructions,
        getCreateTokenAccountInstructions,
        getCreateMintInstructions,
        getMintToInstructions,
        getCreateTokenAccountsInstructions,
        getTokenAccountBalance,
        getWalletTokenAccountByMint,
        burnAllTokenAccountsWithJito,
    } = useSplToken();

    const urlParams = useUrlParams();
    const { scannerConnectionIndex } = urlParams;

    const [poolBalance, setPoolBalance] = useState<string>('0');
    const [tokensPercentageUi, setTokensPercentageUi] = useState<string>('0');
    const [tokensValueUi, setTokensValueUi] = useState<string>('0');
    const [scannerConnection, setScannerConnection] = useState(connections[0]);

    const [scanningEnabled, setScanningEnabled] = useState<boolean>(true);

    const [isJitoTx, setIsJitoTx] = useState<boolean>(true);
    const [jitoTip, setJitoTip] = useState<number>(removeLiquidityPumpswapJitoTip / LAMPORTS_PER_SOL);
    const [removeLpAmount, setRemoveLpAmount] = useState<string>('0');

    const [burnConfirmation, setBurnConfirmation] = useState<boolean>(false);

    const [tokenToParse, setTokenToParse] = useState<string>('');

    const [baseTokenInitialLiquidity, setBaseTokenInitialLiquidity] = useState<number>(206900000);
    const [quoteTokenInitialLiquidity, setQuoteTokenInitialLiquidity] = useState<number>(84.990359266);
    const [bundleLaunch, setBundleLaunch] = useState<boolean>(true);

    const [autoRemoveGoalSol, setAutoRemoveGoalSol] = useState<number>(0.2);
    const [autoRemoveGoalSolEnabled, setAutoRemoveGoalSolEnabled] = useState<boolean>(true);

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);

    const [countdownStarted, setCountdownStarted] = useState<boolean>(false);
    const [countdownStopped, setCountdownStopped] = useState(false);

    useEffect(() => {
        setScannerConnection(connections[scannerConnectionIndex] || connections[0]);
    }, [scannerConnectionIndex]);

    async function solDisperse(connection: Connection, walletFrom: Keypair, receivers: PublicKey[], values: number[]) {
        if (receivers.length === 0) {
            logMessage('No wallets to disperse to.');
            return;
        }

        const MAX_RETRIES = 5;
        let retryCount = 0;

        let pendingReceivers = [...receivers];
        let pendingValues = [...values];

        logMessage('Starting SOL disperse...');

        while (pendingReceivers.length > 0 && retryCount < MAX_RETRIES) {
            const fromBalance = await connection.getBalance(walletFrom.publicKey);
            const totalRequired = pendingValues.reduce((a, b) => a + b, 0);

            if (fromBalance < totalRequired) {
                logMessage(`Insufficient balance. Needed: ${totalRequired}, Available: ${fromBalance}`);
                return;
            }

            try {
                const signaturesToConfirm = await multipleSolanaTrasfersWithJito(
                    connection,
                    walletFrom,
                    new Array(pendingReceivers.length).fill(walletFrom),
                    pendingReceivers,
                    pendingValues,
                    solManagerJitoTip,
                    logMessage,
                );

                await Promise.all(
                    signaturesToConfirm.map((tx) =>
                        connection.confirmTransaction(
                            {
                                blockhash: tx.blockhash,
                                lastValidBlockHeight: tx.lastValidBlockHeight,
                                signature: tx.signature,
                            },
                            'confirmed',
                        ),
                    ),
                );
            } catch (e) {
                logMessage(`Error during transfer attempt ${retryCount + 1}: ${e}`);
            }

            const currentBalances = await getMultipleSolBalances(connection, pendingReceivers);

            const stillPendingReceivers: PublicKey[] = [];
            const stillPendingValues: number[] = [];

            for (let i = 0; i < pendingReceivers.length; i++) {
                if (currentBalances[i] < pendingValues[i]) {
                    stillPendingReceivers.push(pendingReceivers[i]);
                    stillPendingValues.push(pendingValues[i]);
                }
            }

            if (stillPendingReceivers.length === 0) {
                logMessage('All wallets successfully funded.', 'success');
                return;
            }

            logMessage(`${stillPendingReceivers.length} wallets still underfunded. Retrying...`);

            pendingReceivers = stillPendingReceivers;
            pendingValues = stillPendingValues;
            retryCount++;
        }

        if (retryCount >= MAX_RETRIES) {
            logMessage(`Disperse failed after ${MAX_RETRIES} retries. Some wallets may still be unfunded.`, 'error');
        }
    }

    async function getLpBalance() {
        try {
            if (walletLoaded === false) {
                logMessage('WALLET IS NOT LOADED! ');
                return BigInt(0);
            }
            if (!deployedToken) {
                logMessage('Deployed token is not set!');
                return BigInt(0);
            }
            const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
            const lpMint = await getLpMint(mainConnection, new PublicKey(poolId));
            console.log('lpMint', lpMint.toBase58());
            const lpTokenAccount = getAssociatedTokenAddressSync(lpMint, wallet.publicKey, false, TOKEN_2022_PROGRAM_ID);
            console.log('lpTokenAccount', lpTokenAccount.toBase58());
            const balanceInfo = await mainConnection.getTokenAccountBalance(lpTokenAccount, 'processed');
            const walletLpBalance = BigInt(balanceInfo?.value?.amount ?? '0');

            logMessage('Your LP balance: ' + walletLpBalance + ' ');
            setRemoveLpAmount(walletLpBalance.toString());
            return walletLpBalance;
        } catch (error) {
            //logMessage(`Failed to get LP balance: ${error}`, 'error');
            return BigInt(0);
        }
    }

    async function solCollecting(connection: Connection, wallets: Keypair[], receiver: Keypair) {
        try {
            logMessage('Starting SOL collection...');
            wallets = removeKeypairDuplicates(wallets);

            const MIN_RETRY_BALANCE = 0.0001;
            const MAX_RETRIES = 5;
            let retryCount = 0;

            while (wallets.length > 0 && retryCount < MAX_RETRIES) {
                const publicKeys = wallets.map((wallet) => wallet.publicKey);
                const balances = await getMultipleSolBalances(connection, publicKeys);

                const activeWallets: Keypair[] = [];
                const activeBalances: number[] = [];
                try {
                    for (let i = 0; i < balances.length; i++) {
                        if (wallets[i].publicKey.equals(receiver.publicKey)) {
                            continue;
                        }
                        if (balances[i] > MIN_RETRY_BALANCE) {
                            activeWallets.push(wallets[i]);
                            activeBalances.push(balances[i]);
                        }
                    }

                    if (activeWallets.length === 0) {
                        logMessage('All wallets have been drained.');
                        break;
                    }

                    const receivers = new Array(activeWallets.length).fill(receiver.publicKey);
                    console.log('sender', activeWallets[0].publicKey.toBase58());

                    const signaturesToConfirm = await multipleSolanaTrasfersWithJito(
                        connection,
                        receiver,
                        activeWallets,
                        receivers,
                        activeBalances,
                        solManagerJitoTip,
                        logMessage,
                    );

                    await Promise.all(
                        signaturesToConfirm.map((tx) =>
                            connection.confirmTransaction(
                                {
                                    blockhash: tx.blockhash,
                                    lastValidBlockHeight: tx.lastValidBlockHeight,
                                    signature: tx.signature,
                                },
                                'confirmed',
                            ),
                        ),
                    );
                } catch (txErr) {
                    logMessage(`Error during transfer attempt: ${txErr}`);
                }

                // Retry with remaining wallets
                wallets = activeWallets;
                retryCount++;
                logMessage(`Attempt ${retryCount} finished: Checking balances...`);
            }

            if (retryCount >= MAX_RETRIES) {
                logMessage('Reached maximum retry limit. Some wallets may still have funds.', 'error');
            } else {
                logMessage('Everything is fine this time. Good bye sj.', 'success');
            }
        } catch (e) {
            logMessage(`Fatal error collecting: ${e}`);
        }
    }
    async function parseTokenInfo() {
        const parsedTokenMetadata = await parseToken(tokenToParse);

        const name = parsedTokenMetadata.name;
        const symbol = parsedTokenMetadata.symbol;
        const imageUrl = parsedTokenMetadata.image;

        const description = parsedTokenMetadata.description || '';
        const telegram = parsedTokenMetadata.telegram || '';
        const twitter = parsedTokenMetadata.twitter || '';
        const website = parsedTokenMetadata.website || '';

        return {
            name,
            symbol,
            imageUrl,
            description,
            telegram,
            twitter,
            website,
        };
    }

    async function uploadMetadataToIpfs(
        name: string,
        symbol: string,
        image: string,
        description?: string,
        twitter?: string,
        telegram?: string,
        website?: string,
    ) {
        const tokenMetadata: Record<string, string> = {
            name: name,
            symbol: symbol,
            ...(description && { description }),
            image: image,
            ...(twitter && { twitter }),
            ...(telegram && { telegram }),
            ...(website && { website }),
        };

        const uploadedData = await axios.post('https://api.pinata.cloud/pinning/pinJSONToIPFS', tokenMetadata, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_PINATA_JWT}`,
            },
        });
        const metadataUrl = 'https://gateway.pinata.cloud/ipfs/' + uploadedData.data.IpfsHash;
        console.log('metadataUrl', metadataUrl);
        return metadataUrl;
    }

    async function deployMint(
        connection: Connection,
        payer: Keypair,
        mintAuthority: PublicKey,
        freezeAuthority: PublicKey,
        decimals: number = 6,
        mintAmount: number = 1000000000,
        mutableMetadata: boolean = true,
    ) {
        if (!walletLoaded) throw new Error('Wallet not loaded');

        logMessage('Deploying token...');
        const { name, symbol, imageUrl, description, telegram, twitter, website } = await parseTokenInfo();
        console.log('imageUrl', imageUrl);
        if (imageUrl === undefined) throw new Error('Image upload failed');
        const metadataUrl = await uploadMetadataToIpfs(name, symbol, imageUrl, description, twitter, telegram, website);
        if (metadataUrl === undefined) throw new Error('Metadata upload failed');
        logMessage(`Metadata uploaded to IPFS on this link: ${metadataUrl}`);

        const instructions: TransactionInstruction[] = [];

        const customKeypair = await pvmb.getVanity();

        const [mintInstructions, mint] = await getCreateMintInstructions(connection, payer, mintAuthority, freezeAuthority, decimals, customKeypair);
        instructions.push(...mintInstructions);

        const metadataInstructions = await getUpdateMetadataInstructions(payer, mint.publicKey, name, symbol, metadataUrl, mutableMetadata);
        instructions.push(metadataInstructions);

        const createTokenAccountInstructions = getCreateTokenAccountInstructions(payer, mint.publicKey, payer.publicKey);
        instructions.push(createTokenAccountInstructions);
        const tokenAccount = createTokenAccountInstructions.keys[1].pubkey;

        const mintToInstructions = await getMintToInstructions(
            mint.publicKey,
            tokenAccount,
            mintAuthority,
            BigInt(mintAmount) * BigInt(Math.pow(10, decimals)),
        );
        instructions.push(mintToInstructions);

        const holderAssociatedToken = getAssociatedTokenAddressSync(mint.publicKey, holder.publicKey);

        const transferAmount =
            (BigInt(mintAmount) * BigInt(Math.pow(10, decimals)) * BigInt(CreateTokenTransferToHolderDefaultPercentage * 100)) / BigInt(10000);
        const createInputTokenAccountInstructions = createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            holderAssociatedToken,
            holder.publicKey,
            mint.publicKey,
        );
        instructions.push(createInputTokenAccountInstructions);

        const holderAccount = createInputTokenAccountInstructions.keys[1].pubkey;
        const transferToInstructions = createTransferInstruction(tokenAccount, holderAccount, wallet.publicKey, transferAmount);
        instructions.push(transferToInstructions);

        const [currentMintAuthorityPublicKey] = getSigners(wallet.publicKey, []);
        const setMintAuthInstructions = createSetAuthorityInstruction(
            mint.publicKey,
            currentMintAuthorityPublicKey,
            AuthorityType.MintTokens,
            null,
            [],
            TOKEN_PROGRAM_ID,
        );
        instructions.push(setMintAuthInstructions);

        const [currentFreezeAuthorityPublicKey] = getSigners(wallet.publicKey, []);
        const setFreezeAuthInstructions = createSetAuthorityInstruction(
            mint.publicKey,
            currentFreezeAuthorityPublicKey,
            AuthorityType.FreezeAccount,
            null,
            [],
            TOKEN_PROGRAM_ID,
        );
        instructions.push(setFreezeAuthInstructions);

        const priorityFeeInstruction = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: createTokenPriorityFee });
        instructions.push(priorityFeeInstruction);

        const { lastValidBlockHeight, blockhash } = await connection.getLatestBlockhash();
        const deployMintMessage = new TransactionMessage({
            payerKey: wallet.publicKey,
            recentBlockhash: blockhash,
            instructions: instructions,
        }).compileToV0Message();

        const deployTransaction = new VersionedTransaction(deployMintMessage);
        deployTransaction.sign([wallet, mint]);
        console.log('length', deployTransaction.serialize().length);

        await mainConnection.sendTransaction(deployTransaction, { skipPreflight: true });

        await connection.confirmTransaction(
            { blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: bs58.encode(deployTransaction.signatures[0]) },
            'confirmed',
        );

        logMessage('Transaction sent and confirmed');

        setDeployedToken(mint.publicKey.toBase58());
        setDeployedTokenName(name);
        setDeployedTokenSymbol(symbol);
        logMessage(`Token has been minted: ${mint.publicKey.toBase58()}`);
        logMessage(`The token has been successfully deployed. The market can now be created.`, 'success');

        return mint.publicKey.toBase58();
    }

    async function sendWave(
        connection: Connection,
        parents: Keypair[],
        sons: Map<string, Keypair[]>,
        spendPromile: number = 8500,
        mint: string = deployedToken,
    ) {
        // const transferTransactions: VersionedTransaction[] = [];

        console.log(
            'parents',
            parents.map((keypair) => keypair.publicKey.toBase58()),
        );

        const waveInstructions: TransactionInstruction[] = [];
        const waveSigners: Keypair[] = [];
        for (const wallet of parents) {
            const parentTokenAccount = await getWalletTokenAccountByMint(connection, wallet.publicKey, new PublicKey(mint));
            const parentBalance = (await getTokenAccountBalance(connection, parentTokenAccount[0].pubkey)).amount;
            console.log('parent balance', parentBalance);
            if (parentBalance === 0) {
                continue;
            }
            const children = sons.get(wallet.publicKey.toBase58())!;
            if (children.length === 0) {
                continue;
            }
            const randomSpendArray = getRandomizedArray(spendPromile, children.length, 20);
            console.log('random spend array', randomSpendArray);
            for (const [index, child] of children.entries()) {
                //try {
                console.log(
                    `parent wallet: ${wallet.publicKey.toBase58()} => child: ${child.publicKey.toBase58()} => amount: ${Math.floor(
                        (randomSpendArray[index] * parentBalance) / 10000 / 10 ** 6,
                    )}`,
                );
                const childTokenAccount = getAssociatedTokenAddressSync(new PublicKey(mint), child.publicKey);
                const transferInstruction = createTransferInstruction(
                    parentTokenAccount[0].pubkey,
                    childTokenAccount,
                    wallet.publicKey,
                    Math.floor((randomSpendArray[index] * parentBalance) / 10000),
                );

                waveInstructions.push(transferInstruction);
                waveSigners.push(wallet);
            }
        }

        return { waveInstructions, waveSigners };
    }

    async function mixTokenOneWave(mint: string = deployedToken) {
        await checkMixBalances();
        logMessage('Mixing token in one wave...');
        console.log('mint', mint);
        const sons = new Map<string, Keypair[]>();
        function distributeSons(currentWaveWallets: Keypair[], nextWaveWallets: Keypair[]) {
            const numWallets = currentWaveWallets.length;
            const numSons = nextWaveWallets.length;
            const baseSonsNumber = Math.floor(numSons / numWallets);
            const remainder = numSons % numWallets;

            for (let i = 0; i < numWallets; i++) {
                const sonsStart = i * baseSonsNumber + Math.min(i, remainder);
                const sonsEnd = sonsStart + baseSonsNumber + (i < remainder ? 1 : 0);
                const localSons = nextWaveWallets.slice(sonsStart, sonsEnd);
                sons.set(currentWaveWallets[i].publicKey.toBase58(), localSons);
            }
        }

        const wave1Size = mixWallets.length;

        let wave0Wallets: Keypair[] = [];

        wave0Wallets.push(holder);
        wave0Wallets = removeKeypairDuplicates(wave0Wallets);
        if (wave0Wallets.length === 0) {
            return logMessage('No wallets with balance selected');
        }
        const wave1Wallets = mixWallets.slice(0, wave1Size);

        console.log(wave0Wallets.map((keypair) => keypair.publicKey.toBase58()));
        for (const wallet of [...wave0Wallets]) {
            sons.set(wallet.publicKey.toBase58(), []);
        }

        distributeSons(wave0Wallets, wave1Wallets);

        const { transactionsFullInstructions: createAccountsInstructions, signers: createAccountsSigners } = await getCreateTokenAccountsInstructions(
            mainConnection,
            [...wave0Wallets, ...mixWallets],
            new PublicKey(mint),
        );
        const { waveInstructions, waveSigners } = await sendWave(mainConnection, wave0Wallets, sons, 10000, mint);

        logMessage(`Instruction created, sending bundles...`);
        const ataTxs = await executeInstructionsRpc(
            mainConnection,
            [...createAccountsInstructions],
            [...createAccountsSigners],
            funder,
            50000,
            false,
            logMessage,
        );
        const { blockhash, lastValidBlockHeight } = await mainConnection.getLatestBlockhash('confirmed');
        for (const sig of ataTxs) {
            await mainConnection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'processed');
        }
        logMessage(`Token accounts created successfully...`);

        const sendTxs = await executeInstructionsRpc(mainConnection, [...waveInstructions], [...waveSigners], funder, 50000, false, logMessage);
        for (const sig of sendTxs) {
            await mainConnection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'processed');
        }
        logMessage('Token mixing bundles sent and confirmed.', 'success');
    }

    async function deployAmmPool(mint: string = deployedToken) {
        try {
            console.log('mint', mint);
            logMessage('Deploying AMM pool...');
            const { poolAddress, deployIxs } = await buildCreatePoolTx(
                mainConnection,
                mint,
                baseTokenInitialLiquidity,
                6,
                quoteTokenInitialLiquidity,
                wallet.publicKey.toBase58(),
            );
            const tipIx = await getTipInstructions(wallet, deployPoolJitoTipDefault);

            const message = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: (await mainConnection.getLatestBlockhash('finalized')).blockhash,
                instructions: [...deployIxs, tipIx],
            }).compileToV0Message();
            const versionedTx = new VersionedTransaction(message);
            versionedTx.sign([wallet]);
            const bundle: VersionedTransaction[] = [versionedTx];
            logMessage(`Deploying pool on: ${poolAddress}`);

            if (bundleLaunch) {
                //first 4 buyers
                const bundleBuyers = bundleBuyWallets.slice(0, 4);
                const buyValues = bundleBuyValues.slice(0, 4);

                const buyTxs = bundleBuyers.map(async (buyer, index) => {
                    const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(mint));
                    const ix = getSellIx(
                        poolId,
                        new PublicKey(WSOL.mint),
                        new PublicKey(mint),
                        buyer.publicKey,
                        BigInt(buyValues[index] * LAMPORTS_PER_SOL),
                        BigInt(0),
                    );

                    const tokenAta = getAssociatedTokenAddressSync(new PublicKey(mint), buyer.publicKey);
                    const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), buyer.publicKey);
                    const accIxs = [
                        createAssociatedTokenAccountIdempotentInstruction(buyer.publicKey, wsolAta, buyer.publicKey, new PublicKey(WSOL.mint)),
                        SystemProgram.transfer({
                            fromPubkey: buyer.publicKey,
                            toPubkey: wsolAta,
                            lamports: BigInt(buyValues[index] * LAMPORTS_PER_SOL + 10001),
                        }),
                        createSyncNativeInstruction(wsolAta),
                        createAssociatedTokenAccountIdempotentInstruction(buyer.publicKey, tokenAta, buyer.publicKey, new PublicKey(mint)),
                    ];

                    const tx = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: buyer.publicKey,
                            recentBlockhash: (await mainConnection.getLatestBlockhash('finalized')).blockhash,
                            instructions: [...accIxs, ix],
                        }).compileToV0Message(),
                    );
                    tx.sign([buyer]);
                    return tx;
                });

                const resolvedBuyTxs = await Promise.all(buyTxs);

                bundle.push(...resolvedBuyTxs);
            }

            await spamAndConfirmBundle(mainConnection, bundle, wallet, deployPoolJitoTipDefault, true, 1000, 30000, true);
            setCountdownStarted(true);
            logMessage(`Transaction sent: ${bs58.encode(versionedTx.signatures[0])}`, 'success');
        } catch (error) {
            console.error('Error deploying AMM pool:', error);
            logMessage(`Error deploying AMM pool: ${error}`, 'error');
        }
    }

    const [profitReachedSlots, setProfitReachedSlots] = useState<number>(0);
    const [lastProfitReachedSlot, setLastProfitReachedSlot] = useState<number>(0);

    useEffect(() => {
        if (autoRemoveGoalSolEnabled) {
            if (autoRemoveGoalSol * LAMPORTS_PER_SOL > Number(tokensValueUi) * LAMPORTS_PER_SOL) {
                console.debug(`Auto remove goal not reached: ${autoRemoveGoalSol * LAMPORTS_PER_SOL} < ${Number(tokensValueUi)}`);
                setProfitReachedSlots(0);
                return;
            }
            if (currentSlot === lastProfitReachedSlot) {
                console.debug(`Current slot is the same as last profit reached slot: ${currentSlot}`);
                return;
            }
            logMessage(`Auto removeP: Goal reached. Current number of slots: ${profitReachedSlots + 1}`, 'success');
            // logMessage(`Auto remove goal reached`, 'success');
            if (profitReachedSlots + 1 >= removeLiquidityPumspwapGoalReachedTicksToRemove) {
                logMessage(`Profit found for ${removeLiquidityPumspwapGoalReachedTicksToRemove} tick(s) already, removing liquidity`);
                removeLiquidity(Number(removeLpAmount).toString());
                setAutoRemoveGoalSolEnabled(false);
            }
            setProfitReachedSlots((prev) => prev + 1);
            setLastProfitReachedSlot(currentSlot);
        }
    }, [profitReachedSlots, currentSlot, tokensValueUi, autoRemoveGoalSolEnabled, autoRemoveGoalSol, removeLpAmount, lastProfitReachedSlot]);

    async function removeLiquidity(removeLpAmount: string, burnAcc: boolean = false) {
        try {
            logMessage(`Removing ${removeLpAmount} LP tokens`);
            const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));

            let burnIx: TransactionInstruction | undefined = undefined;
            if (burnAcc) {
                const lpMint = await getLpMint(mainConnection, new PublicKey(poolId));
                console.log('lpMint', lpMint.toBase58());
                const lpTokenAccount = getAssociatedTokenAddressSync(lpMint, wallet.publicKey, false, TOKEN_2022_PROGRAM_ID);
                console.log(lpTokenAccount);
                burnIx = createCloseAccountInstruction(lpTokenAccount, wallet.publicKey, wallet.publicKey, [], TOKEN_2022_PROGRAM_ID);
            }

            const removeLiquidityTx = await buildRemoveLiquidityTx(
                mainConnection,
                poolId.toBase58(),
                Number(removeLpAmount),
                wallet.publicKey.toBase58(),
                removeLiquidityPumpswapJitoTip,
                removeLiquidityPumpswapRemoveUnits,
                removeLiquidityPumpswapRemovePriorityGas,
                burnIx ? [burnIx] : [],
            );

            removeLiquidityTx.sign([wallet]);

            const bundleId = await send0SlotTransaction(removeLiquidityTx);
            logMessage(`Bundle sent: ${bundleId}`);
        } catch (error) {
            logMessage(`Failed to remove liquidity ${error}`);
        }
    }

    useEffect(() => {
        if (autoActionStarted) return;
        if (!deployedToken) return;

        if (autoAction === 'poolScanner') {
            logMessage('Starting auto action for pool scanner.');
            setScanningEnabled(true);
            setScanningEnabled(true);
            setAutoActionStarted(true);
        }

        // const timeDifference = Date.now() - new Date(updatedAt).getTime();
        // if (timeDifference > 30000) return;
        // if (autoAction === 'tSell') {
        //     logMessage('Starting auto action for remove liquidity.');
        //     setScanningEnabled(true);
        //     setAutoActionStarted(true);
        // }
    }, [deployedToken, autoAction, autoActionStarted]);

    useEffect(() => {
        if (!scanningEnabled) return;
        if (!deployedToken) return;
        console.debug('scanner');

        const fetchTokenValues = async () => {
            try {
                const noDuplicatesWallets = removeKeypairDuplicates(allWallets);
                const walletAtaPubkeys = await Promise.all(
                    noDuplicatesWallets.map((wallet) => getAssociatedTokenAddress(new PublicKey(deployedToken), wallet.publicKey)),
                );

                const getMultipleAccountInfosChunked = async (pubKeys: Array<PublicKey>, slot: number) => {
                    const promises = [];

                    for (let i = 0; i < pubKeys.length; i += SCANNER_CHUNK_SIZE)
                        promises.push(scannerConnection.getMultipleAccountsInfo(pubKeys.slice(i, i + SCANNER_CHUNK_SIZE), { minContextSlot: slot }));

                    const result = await Promise.all(promises);

                    return result.flat();
                };

                const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                const poolBaseTokenAccount = await getAssociatedTokenAddress(new PublicKey(WSOL.mint), poolId, true);
                const poolQuoteTokenAccount = await getAssociatedTokenAddress(new PublicKey(deployedToken), poolId, true);

                const [wsolVaultInfo, tokenVaultInfo, mintAccountInfo, ...walletAtaInfos] = await getMultipleAccountInfosChunked(
                    [poolBaseTokenAccount, poolQuoteTokenAccount, new PublicKey(deployedToken), ...walletAtaPubkeys],
                    currentSlot,
                );

                const mint = unpackMint(new PublicKey(deployedToken!), mintAccountInfo);
                const unpackAccountBalance = (info: AccountInfo<Buffer> | null | undefined) =>
                    BigInt(info ? SPL_ACCOUNT_LAYOUT.decode(info.data).amount.toString() : 0);

                const feeTokenAccounts = pumpAmmProtocolFees.map((account) =>
                    getAssociatedTokenAddress(new PublicKey(deployedToken), new PublicKey(account), true),
                );
                const feeAccounts = await getMultipleAccountInfosChunked(await Promise.all(feeTokenAccounts), currentSlot);
                const feeBalances = feeAccounts.map((info) => unpackAccountBalance(info));
                const totalFeeBalance = feeBalances.reduce((sum, balance) => sum + balance, BigInt(0));

                const tokenVaultBalance = unpackAccountBalance(tokenVaultInfo);
                const wsolVaultBalance = unpackAccountBalance(wsolVaultInfo);
                const walletBalances = walletAtaInfos.map((info) => unpackAccountBalance(info));

                const sumOfWalletBalances = walletBalances.reduce((sum, balance) => sum + balance, BigInt(0));
                console.log('sum', sumOfWalletBalances);
                console.log('supply', mint.supply);
                const tokens = mint.supply - tokenVaultBalance - sumOfWalletBalances - totalFeeBalance;
                console.log('other tokens', tokens);
                const tokensPercentage = (tokens * BigInt(100)) / mint.supply;

                const tokensValue = (wsolVaultBalance * tokens) / (tokenVaultBalance + tokens);

                console.log('tokensValue', tokensValue, 'wsolVaultBalance', wsolVaultBalance, 'tokenVaultBalance', tokenVaultBalance);
                setTokensPercentageUi(tokensPercentage.toString());
                setTokensValueUi(new CurrencyAmount(new Currency(WSOL.decimals), tokensValue).toFixed(2));
            } catch (error) {
                console.log(error);
            }
        };

        const fetchPoolBalance = async () => {
            try {
                const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                const poolBaseTokenAccount = await getAssociatedTokenAddress(new PublicKey(WSOL.mint), poolId, true);
                const pow = 9;

                const accountInfo = await scannerConnection.getAccountInfo(poolBaseTokenAccount, { minContextSlot: currentSlot });
                const account = unpackAccount(poolBaseTokenAccount, accountInfo!);
                const balanceString = bigIntToStringWithDecimals(account.amount, pow);
                console.log(`balanceString`, balanceString);
                setPoolBalance(balanceString);

                if (removeLpAmount === '0') {
                    getLpBalance();
                }
            } catch (error) {
                console.log(error);
            }
        };
        try {
            fetchTokenValues();
            fetchPoolBalance();
        } catch (e) {
            console.log(e);
            logMessage('RPC error in scanner', 'error');
        }
    }, [deployedToken, allWallets, scannerConnection, isJitoTx, currentSlot, scanningEnabled, autoRemoveGoalSol, autoRemoveGoalSolEnabled, removeLpAmount]);

    useEffect(() => {
        if (!deployedToken) return;
        try {
            getLpBalance();
        } catch (e) {
            console.log(e);
        }
    }, [deployedToken]);

    async function burnAllTokenAccounts(connection: Connection, unlockedSolReceiver: PublicKey, walletsFrom: Keypair[], jitoTip: number, funder: Keypair) {
        const MAX_RETRIES = 5;
        let retryCount = 0;

        walletsFrom = removeKeypairDuplicates(walletsFrom);

        walletsFrom = walletsFrom.filter((wal) => wal.publicKey.toBase58() !== wallet.publicKey.toBase58());

        logMessage('Burning all token accounts...');
        logMessage(`Found ${walletsFrom.length} unique wallets`);
        console.log(walletsFrom.map((wallet) => wallet.publicKey.toBase58()));

        while (retryCount < MAX_RETRIES) {
            try {
                await burnAllTokenAccountsWithJito(connection, unlockedSolReceiver, walletsFrom, jitoTip, funder, logMessage);

                logMessage('All token accounts burned', 'success');
                return;
            } catch (e) {
                retryCount++;
                logMessage(`Error during burn attempt ${retryCount}: ${e}`);

                if (retryCount >= MAX_RETRIES) {
                    logMessage('Burning failed after max retries. Some tokens may still exist.', 'error');
                    return;
                }

                logMessage('Retrying burn process...');
            }
        }
    }

    async function fullLaunch() {
        const mint = await deployMint(mainConnection, wallet, wallet.publicKey, wallet.publicKey).catch((error) => {
            logMessage(`Error deploying mint: ${error}`, 'error');
            return;
        });
        if (!mint) {
            logMessage('Token deployment failed, aborting full launch.', 'error');
            return;
        }
        await mixTokenOneWave(mint).catch((error) => {
            logMessage(`Error mixing token: ${error}`, 'error');
            return;
        });

        await deployAmmPool(mint).catch((error) => {
            logMessage(`Error deploying AMM pool: ${error}`, 'error');
            return;
        });
    }

    async function checkMixBalances() {
        const noDuplicatesWallets = removeKeypairDuplicates(mixWallets);
        const balances = await getMultipleSolBalances(
            mainConnection,
            noDuplicatesWallets.map((wallet) => wallet.publicKey),
        );
        const insufficientBalanceWallets = noDuplicatesWallets.filter((wallet, index) => balances[index] < solManagerDisperseForMixWalletFees);

        if (insufficientBalanceWallets.length > 0) {
            logMessage(`Mix wallets with insufficient balance: ${insufficientBalanceWallets.length}`, 'error');
            await solDisperse(
                mainConnection,
                wallet,
                insufficientBalanceWallets.map((wallet) => wallet.publicKey),
                insufficientBalanceWallets.map(() => solManagerDisperseForMixWalletFees),
            );

            return false;
        }

        logMessage('All wallets have sufficient balance for mixing.');
        return true;
    }

    async function onTimeout() {
        const lpBalance = await getLpBalance();
        if (lpBalance === BigInt(0)) {
            return;
        }
        logMessage(`Timeout reached, removing liquidity...`);
        removeLiquidity(lpBalance.toString(), true).catch((error) => {
            logMessage(`Error removing liquidity: ${error}`, 'error');
        });
    }

    return (
        <section>
            <h1> Rapid Launch Pumpswap</h1>

            <h2>Preparations</h2>
            <section className='preparations'>
                <button
                    onClick={() => {
                        const allReceivers = [
                            ...bundleBuyWallets.map((wallet) => wallet.publicKey),
                            ...[funder.publicKey, holder.publicKey, freezer.publicKey, volFunder?.publicKey!],
                            ...volumeBuyWallets.map((wallet) => wallet.publicKey),
                            ...mixWallets.map((wallet) => wallet.publicKey),
                        ];
                        const allValues = [
                            ...bundleBuyValues.map((bundleBuyValue) => bundleBuyValue * LAMPORTS_PER_SOL + solManagerDisperseForBundleBuyFeesExtra),
                            ...[0.25 * LAMPORTS_PER_SOL, 0.1 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL, 0.25 * LAMPORTS_PER_SOL],
                            ...volumeBuyAmounts.map((amount) => amount * LAMPORTS_PER_SOL + solManagerVolumeBuyExtra),
                            ...new Array(mixWallets.length).fill(solManagerDisperseForMixWalletFees),
                        ];

                        solDisperse(mainConnection, wallet, allReceivers, allValues);
                    }}
                >
                    Disperse All SOL
                </button>
                <button
                    onClick={() => {
                        solCollecting(mainConnection, [...allWallets, ...volumeBuyWallets], wallet);
                    }}
                >
                    Collect All SOL
                </button>
                <button
                    onClick={async () => {
                        const balance = await mainConnection.getBalance(wallet.publicKey, 'processed');
                        if (balance < LAMPORTS_PER_SOL) {
                            logMessage('Source wallet does not have enough SOL to disperse.', 'error');
                            return;
                        }
                        solDisperse(
                            mainConnection,
                            wallet,
                            volumeBuyWallets.map((wallet) => wallet.publicKey),
                            getRandomizedArray(balance - LAMPORTS_PER_SOL, volumeBuyWallets.length, 50).map((amount) => Math.floor(amount)),
                        );
                    }}
                >
                    VolTopup
                </button>
            </section>
            <h2>Express Token Create</h2>
            <section className='continue-mix'>
                <input
                    value={tokenToParse}
                    onChange={(event) => {
                        let link = event.target.value;

                        if (link.includes('pump.fun')) {
                            try {
                                const url = new URL(link);
                                const path = url.pathname.split('/').pop();
                                setTokenToParse(path || '');
                            } catch (error) {
                                console.error('Invalid URL format:', error);
                                setTokenToParse(link);
                            }
                        } else {
                            setTokenToParse(link);
                        }
                    }}
                ></input>
                <button
                    className='button-predisperse'
                    onClick={() => {
                        deployMint(mainConnection, wallet, wallet.publicKey, wallet.publicKey);
                    }}
                >
                    Parse & Create
                </button>
                <button className='button-predisperse' onClick={fullLaunch}>
                    Parse & Automize All
                </button>
            </section>

            <h2>Disperse Tokens</h2>

            <section className='continue-mix'>
                <button onClick={() => mixTokenOneWave(deployedToken)}>Token Mix 1 Wave</button>
            </section>
            <h2>Liquidity</h2>
            <section className='continue-mix'>
                <div className='percent-every'>
                    <input
                        type='number'
                        value={quoteTokenInitialLiquidity}
                        onChange={(e) => {
                            setQuoteTokenInitialLiquidity(Number(e.target.value));
                        }}
                    />
                    SOL
                </div>
                <div className='milliseconds'>
                    <input
                        type='number'
                        value={baseTokenInitialLiquidity}
                        onChange={(e) => {
                            setBaseTokenInitialLiquidity(Number(e.target.value));
                        }}
                    />
                    Tokens
                </div>
                <div className='jitotx'>
                    <input
                        type='checkbox'
                        className='jito-tx'
                        checked={bundleLaunch}
                        onChange={(e) => {
                            setBundleLaunch(e.target.checked);
                        }}
                    />
                    Bundle Buy
                </div>
                <div>
                    <button onClick={() => deployAmmPool(deployedToken)}>GO</button>
                </div>
            </section>
            <section>
                <h2>Scanner</h2>
            </section>
            <section className='scan-deploy-btn'>
                <div className='jito-tx-check-inp'>
                    <input
                        type={'checkbox'}
                        checked={autoRemoveGoalSolEnabled}
                        onChange={(e) => {
                            setAutoRemoveGoalSolEnabled(e.target.checked);
                        }}
                    ></input>
                    Auto Remove At
                </div>

                <div className='buy-range'>
                    <input
                        type={'number'}
                        value={autoRemoveGoalSol}
                        onChange={(e) => {
                            setAutoRemoveGoalSol(Number(e.target.value));
                        }}
                    ></input>
                    Sol Profit
                </div>

                {countdownStarted && (
                    <div className='countdown-timer'>
                        <CountdownTimer durationSeconds={rapidPumpfunTimeoutRemoveSeconds} onTimeout={onTimeout} stopped={countdownStopped} />
                        <button onClick={() => setCountdownStopped((e) => !e)}>Stop Timeout</button>
                    </div>
                )}
            </section>

            <section className='liq-sum-tok'>
                <div className='liq-text'>
                    <div className='liq'></div>
                    <h1>{poolBalance}</h1>
                </div>
                <div className='sum-text'>
                    <div className='sum'></div>
                    <h1> {(Number(poolBalance) + totalWalletsBalance / LAMPORTS_PER_SOL).toFixed(3)}</h1>
                </div>
                <div className='tok-text'>
                    <div className='tok'></div>
                    <div className='text'>
                        <div className='text-tok'>Tokens: {tokensPercentageUi}%</div>
                        <div className='text-sol'>SOL {tokensValueUi}</div>
                    </div>

                    <section className='list'>
                        <div className='sel-list-text'>
                            <select
                                value={connections.findIndex((connection) => connection.rpcEndpoint == scannerConnection.rpcEndpoint)}
                                onChange={(event) => setScannerConnection(connections[Number(event.target.value)])}
                            >
                                {connections.map((connection, index) => (
                                    <option value={index} key={index}>
                                        {new URL(connection.rpcEndpoint).host}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </section>
                </div>
            </section>
            <section className='scan-deploy-btn'>
                <div className='scan-btn'>
                    <button className='scan' onClick={() => setScanningEnabled(true)}>
                        <div className='icon-scan'></div>Scan Pool Balance
                    </button>
                </div>
                <div className='jito'>
                    Tip
                    <input type='value' className='jito-tip' value={jitoTip} onChange={(e) => setJitoTip(Number(e.target.value))} />
                </div>
            </section>
            <section className='remove-get-btn'>
                <div className='remove-lp-buttons'>
                    <button className='button-150' onClick={() => removeLiquidity(Math.floor((Number(removeLpAmount) * 999) / 1000).toString())}>
                        Remove 99.9%
                    </button>
                    <button className='button-150' onClick={() => removeLiquidity(removeLpAmount, true)}>
                        Remove 100%
                    </button>
                    0Slot REMOVE RPC
                    <input
                        type={'checkbox'}
                        checked={isJitoTx}
                        onClick={() => {
                            setIsJitoTx((prev) => !prev);
                        }}
                    ></input>
                    <button className='button-accent' onClick={() => getLpBalance()}>
                        Get Wallet LP Balance
                    </button>
                </div>
            </section>

            <h2>Finish</h2>
            <section className='burn-section'>
                <section className='burn-container'>
                    <div className='must-check'>
                        <input
                            type='checkbox'
                            checked={burnConfirmation}
                            onChange={({ target: { checked } }) => setBurnConfirmation(checked)}
                            className='must-check-to-burn'
                        />
                        MUST CHECK TO BURN
                    </div>
                </section>
                <section className='burn-btns'>
                    <div className='btns'>
                        <button
                            onClick={() => {
                                burnAllTokenAccounts(mainConnection, wallet.publicKey, [...allWallets, ...volumeBuyWallets], tokenManagerJitoTip, wallet);
                            }}
                            disabled={!burnConfirmation}
                            className='button-danger'
                        >
                            BURN ALL TOKENS
                        </button>
                    </div>
                </section>
            </section>
        </section>
    );
}
